import { Body2, Link as FluentLink, Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components';
import { Link } from 'react-router-dom';
import FileIcon from '@modules/items/components/FileIcon';

import { getFindingDocumentHref, getFindingDocumentType } from '../../helpers';
import { TFindingDocument } from '../../types';

const DocumentLink = ({ document }: { document: TFindingDocument }) => {
  const href = getFindingDocumentHref(document);
  const content = (
    <>
      <span className="shrink-0">
        <FileIcon
          type={getFindingDocumentType(document)}
          mimetype={document.mimetype ?? (/\.pdf$/i.test(document.name) ? 'application/pdf' : undefined)}
          name={document.name}
          path={[]}
          isBlock={false}
          isFrozen={false}
          isTemplate={false}
          size="small"
        />
      </span>
      {/* Every source reads as a link, also one without a document to open yet. */}
      <Body2 className="break-words min-w-0 [overflow-wrap:anywhere]">
        <FluentLink as="span">{document.name}</FluentLink>
      </Body2>
    </>
  );

  return href ? (
    <Link to={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 min-w-0">
      {content}
    </Link>
  ) : (
    <div className="inline-flex items-center gap-2 min-w-0">{content}</div>
  );
};

export const FindingDocuments = ({ documents = [], limit }: { documents?: TFindingDocument[]; limit?: number }) => {
  const unique = documents.filter(
    (document, index) =>
      documents.findIndex((candidate) =>
        candidate.documentId && document.documentId
          ? candidate.documentId === document.documentId
          : candidate.name === document.name
      ) === index
  );
  const visible = limit === undefined ? unique : unique.slice(0, limit);
  const remaining = unique.slice(visible.length);

  if (!unique.length) return null;

  return (
    <div className="flex flex-col items-start gap-3">
      {visible.map((document) => (
        <DocumentLink key={document.documentId ?? document.name} document={document} />
      ))}
      {remaining.length ? (
        <Popover positioning="below-start">
          <PopoverTrigger disableButtonEnhancement>
            <FluentLink
              as="button"
              className="!text-neutralFg-3 !text-sm !font-normal"
              aria-label={`Show ${remaining.length} more document${remaining.length === 1 ? '' : 's'}`}
            >
              +{remaining.length} document{remaining.length === 1 ? '' : 's'}
            </FluentLink>
          </PopoverTrigger>
          <PopoverSurface className="max-w-xl">
            <div className="flex flex-col gap-3">
              {remaining.map((document) => (
                <DocumentLink key={document.documentId ?? document.name} document={document} />
              ))}
            </div>
          </PopoverSurface>
        </Popover>
      ) : null}
    </div>
  );
};
